// First-party marketing attribution — capture where a buyer came from.
//
// On a *campaigned* landing (URL carries any utm_* param or X's `twclid` click
// id) we persist that attribution to localStorage as the "last campaigned
// touch", along with the external referrer and the landing path. Plain internal
// navigation never overwrites it, so the signal survives until the buyer checks
// out, at which point the order-create request forwards these fields and the
// server stores them on the order (visible in the admin).
//
// Last-touch (not first-touch) because the question we care about is "which ad /
// campaign drove this sale" — the most recent campaigned click before purchase.

const STORAGE_KEY = 'raspicks:attribution';

// Don't credit a purchase to a campaign click older than this.
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000;

// Stripe metadata caps values at 500 chars; keep everything well under.
const MAX_LEN = 400;

export type Attribution = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
  twclid?: string;
  referrer?: string;
  landingPage?: string;
};

type StoredAttribution = Attribution & { at: string };

const clip = (value: string | null | undefined): string | undefined => {
  const trimmed = (value || '').trim();
  return trimmed ? trimmed.slice(0, MAX_LEN) : undefined;
};

/**
 * Read the URL for campaign signals. Returns the attribution fields present, or
 * null when this isn't a campaigned landing (so we leave stored data untouched).
 */
function readIncoming(): Attribution | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const utmSource = clip(params.get('utm_source'));
  const utmMedium = clip(params.get('utm_medium'));
  const utmCampaign = clip(params.get('utm_campaign'));
  const utmTerm = clip(params.get('utm_term'));
  const utmContent = clip(params.get('utm_content'));
  const twclid = clip(params.get('twclid'));

  // Only treat this as an attributable visit if a campaign signal is present.
  if (!utmSource && !utmMedium && !utmCampaign && !utmTerm && !utmContent && !twclid) {
    return null;
  }

  // External referrer only — internal (same-host) referrers aren't a source.
  let referrer: string | undefined;
  try {
    const ref = typeof document !== 'undefined' ? document.referrer : '';
    if (ref && new URL(ref).hostname !== window.location.hostname) {
      referrer = clip(ref);
    }
  } catch {
    referrer = undefined;
  }

  const landingPage = clip(`${window.location.pathname}${window.location.search}`);

  return {
    utmSource,
    utmMedium,
    utmCampaign,
    utmTerm,
    utmContent,
    twclid,
    referrer,
    landingPage,
  };
}

/**
 * Read a bare external referrer (a different-hostname document.referrer) when
 * the URL carries no campaign signal. This is the common "someone linked us"
 * case with no utm tagging: an X app tap arrives via t.co, a Substack post via
 * substack.com, an organic search via google.com. Returns just the referrer (no
 * utm/twclid) so the admin can surface a real source instead of "Direct", or
 * null when there's no usable external referrer.
 *
 * Note: SPA route changes don't reset document.referrer, so on internal
 * navigation this keeps returning the original external referrer — harmless,
 * since the gap-fill below only writes when nothing is already stored.
 */
function readExternalReferrer(): Attribution | null {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return null;
  }
  try {
    const ref = document.referrer;
    if (!ref || new URL(ref).hostname === window.location.hostname) {
      return null; // no referrer, or an internal one — not a source
    }
    const referrer = clip(ref);
    if (!referrer) {
      return null;
    }
    return {
      referrer,
      landingPage: clip(`${window.location.pathname}${window.location.search}`),
    };
  } catch {
    return null;
  }
}

/**
 * Call once per client render. If the current URL is a campaigned landing,
 * store it (overwriting any prior campaigned touch). Otherwise, when the visit
 * arrived from an external referrer and nothing is already stored, record that
 * bare referrer as a gap-filling touch (never overwriting a real campaigned
 * touch — utm/twclid always wins). No-op on plain internal navigation.
 */
export function captureAttribution(): void {
  try {
    const incoming = readIncoming();
    if (incoming) {
      const record: StoredAttribution = { ...incoming, at: new Date().toISOString() };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
      return;
    }

    // No campaign signal on this URL. Fall back to a bare external referrer, but
    // only to fill a gap — never clobber an existing fresh touch, so paid/social
    // attribution captured earlier always survives.
    const referrerTouch = readExternalReferrer();
    if (!referrerTouch || Object.keys(getAttribution()).length > 0) {
      return;
    }
    const record: StoredAttribution = { ...referrerTouch, at: new Date().toISOString() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // localStorage can throw (private mode, quota); attribution is best-effort.
  }
}

/**
 * Force-store an attribution touch for an unlisted funnel landing that carries
 * no utm/twclid signal, e.g. /vsin, which VSIN promotes on-air as a bare URL
 * that listeners type directly. captureAttribution() would no-op there (no
 * campaign signal in the URL), so the funnel would be invisible to attribution.
 * This writes the record unconditionally, overwriting any prior campaigned touch
 * (last-touch model, same as a real utm landing would). Best-effort; localStorage
 * can throw in private mode.
 */
export function captureLanding(fields: Attribution): void {
  try {
    if (typeof window === 'undefined') {
      return;
    }
    const record: StoredAttribution = { ...fields, at: new Date().toISOString() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // localStorage can throw (private mode, quota); attribution is best-effort.
  }
}

/**
 * Stamp a fallback funnel source for a self-promoting landing page (e.g. the
 * unlisted /cfb-results promo) — but ONLY for a visitor who carries no inbound
 * campaign signal and has no prior campaigned touch stored. Unlike
 * captureLanding(), this NEVER overwrites a real inbound utm/twclid landing or an
 * earlier stored touch, so paid/social attribution is always preserved; it only
 * fills the gap for direct / organic-search visitors who would otherwise reach
 * checkout with no source at all. Best-effort; localStorage can throw.
 */
export function captureFallbackLanding(fields: Attribution): void {
  try {
    if (typeof window === 'undefined') {
      return;
    }
    // Inbound campaign signal on this URL — captureAttribution() owns it. Bail.
    if (readIncoming()) {
      return;
    }
    // A fresh (non-stale) campaigned touch is already stored — keep it.
    if (Object.keys(getAttribution()).length > 0) {
      return;
    }
    const record: StoredAttribution = { ...fields, at: new Date().toISOString() };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(record));
  } catch {
    // localStorage can throw (private mode, quota); attribution is best-effort.
  }
}

/**
 * Read the stored attribution for attaching to an order-create request. Returns
 * only defined fields (safe to spread into a request body), or {} when there's
 * no fresh campaigned touch.
 */
export function getAttribution(): Attribution {
  try {
    if (typeof window === 'undefined') {
      return {};
    }

    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {};
    }

    const stored = JSON.parse(raw) as StoredAttribution;
    if (stored?.at && Date.now() - new Date(stored.at).getTime() > MAX_AGE_MS) {
      return {}; // stale — don't attribute an old click to this purchase
    }

    const { at: _at, ...fields } = stored || {};
    // Drop any undefined/empty keys so the request body stays clean.
    return Object.fromEntries(
      Object.entries(fields).filter(([, v]) => typeof v === 'string' && v),
    ) as Attribution;
  } catch {
    return {};
  }
}
